/**
 * Main application layout with sidebar navigation.
 *
 * SPEC FE-1 / R3a (responsive shell): at lg and up the fixed w-64 sidebar renders
 * as before (Navigation). Below lg the sidebar is hidden and a top bar with a
 * hamburger opens the nav in a Sheet drawer, so on a phone the main content uses
 * the full width and the nav is still reachable. The drawer renders the SAME
 * NavContent (same role-gating) as the desktop sidebar.
 */

import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Navigation, NavContent } from './Navigation';
import { Button } from '@/components/ui/button';
import { BRAND_NAME } from '@/lib/brand';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

export function AppLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop sidebar (lg and up) */}
      <Navigation />

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar with hamburger (below lg) */}
        <header className="lg:hidden flex items-center gap-3 border-b bg-card px-4 py-3">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-card p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <NavContent onNavigate={() => setMobileNavOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="font-heading text-base font-semibold tracking-tight">{BRAND_NAME}</span>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
